import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { wabaApi } from "../../../lib/wabaApi";
import { Modal } from "../../shared/Modal";
import { AgentChecklist, AgentPicker, type AgentOption } from "./AgentPicker";
import { btn, btnSm, errorText, input, label, primaryBtn, td, th } from "./common";

/** Contas do portal do cliente (helena.revendai.com). Uma conta vê N agentes
 *  (cada um com a conexão WABA vinculada). Senha gerada no backend e exibida
 *  UMA vez; recuperação manual = botão Resetar senha. */

type AccountAgent = { agent_id: string; agent_name: string | null; connection_name: string | null };

type Account = {
  id: string;
  login: string;
  display_name: string;
  disabled: boolean;
  last_login_at: string | null;
  created_at: string;
  agents?: AccountAgent[];
};

type Conn = { id: string; name: string; agent_id: string | null; agent_name: string | null };

type Cred = { login: string; password: string };
type DashCred = { dash_id: string; dash_key: string };

const MAX_AGENTS = 20;

export function AccountsTab() {
  const qc = useQueryClient();
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [login, setLogin] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [cred, setCred] = useState<Cred | null>(null);
  const [dash, setDash] = useState<DashCred | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Account | null>(null);

  const { data: accounts = [], isLoading } = useQuery<Account[]>({
    queryKey: ["helena-accounts"],
    queryFn: () => wabaApi.get("/portal/accounts"),
  });

  const { data: conns = [] } = useQuery<Conn[]>({
    queryKey: ["helena-conns"],
    queryFn: () => wabaApi.get("/metrics/connections"),
  });
  const options: AgentOption[] = conns
    .filter((c) => c.agent_id)
    .map((c) => ({ id: c.agent_id!, name: c.agent_name ?? c.agent_id!, connection: c.name }));

  const createMut = useMutation({
    mutationFn: () =>
      wabaApi.post<Cred & { id: string }>("/portal/accounts", {
        agent_ids: agentIds, login, display_name: displayName,
      }),
    onSuccess: (r) => {
      setCred({ login: r.login, password: r.password });
      setAgentIds([]); setLogin(""); setDisplayName(""); setError(null);
      qc.invalidateQueries({ queryKey: ["helena-accounts"] });
    },
    onError: (e) => setError(errorText(e, "Não foi possível criar a conta.")),
  });

  const resetMut = useMutation({
    mutationFn: (id: string) => wabaApi.post<Cred>(`/portal/accounts/${id}/reset-password`),
    onSuccess: (r) => { setCred(r); setError(null); },
    onError: (e) => setError(errorText(e, "Não foi possível resetar a senha.")),
  });

  // dashid + dashapikey: o CRM (app.crmillion.com.br) usa pra abrir o portal em
  // iframe já logado. Revelar a qualquer hora; regenerar invalida a anterior.
  const keyMut = useMutation({
    mutationFn: ({ id, regen }: { id: string; regen: boolean }) =>
      regen
        ? wabaApi.post<DashCred>(`/portal/accounts/${id}/regenerate-key`)
        : wabaApi.get<DashCred>(`/portal/accounts/${id}/sso-key`),
    onSuccess: (r) => { setDash(r); setError(null); },
    onError: (e) => setError(errorText(e, "Não foi possível obter a chave.")),
  });

  const toggleMut = useMutation({
    mutationFn: (a: Account) =>
      wabaApi.patch(`/portal/accounts/${a.id}`, { disabled: !a.disabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["helena-accounts"] }),
    onError: (e) => setError(errorText(e, "Não foi possível alterar a conta.")),
  });

  const canCreate = agentIds.length > 0 && login.trim().length >= 3 && !!displayName.trim();

  return (
    <div style={{ maxWidth: 1000, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>
          Contas do portal do cliente
        </div>
        <div style={{ fontSize: 12.5, color: "var(--ink-muted)", lineHeight: 1.5 }}>
          Login e senha para o cliente acessar helena.revendai.com (métricas e conversas dos
          agentes da conta; com 2 ou mais agentes ele escolhe qual ver). A senha é gerada aqui e
          mostrada uma única vez — recuperação é manual, pelo botão Resetar senha.
        </div>
      </div>

      {/* Senha gerada — mostrada uma vez */}
      {cred && (
        <div style={{ padding: "14px 16px", background: "var(--brand-soft)", border: "1px solid var(--brand)", borderRadius: 8, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "var(--ink-muted)", marginBottom: 4 }}>
              Copie agora — a senha não será mostrada de novo.
            </div>
            <code style={{ fontSize: 14, color: "var(--ink)" }}>
              {cred.login} / {cred.password}
            </code>
          </div>
          <button style={btn} onClick={() => navigator.clipboard.writeText(`Acesso ao painel do seu agente de IA\nhttps://helena.revendai.com\nLogin: ${cred.login}\nSenha: ${cred.password}`)}>
            Copiar
          </button>
          <button style={btn} onClick={() => setCred(null)}>Fechar</button>
        </div>
      )}

      {/* Credencial do CRM (dashid + dashapikey) — popup, a lista tem dezenas de linhas */}
      {dash && (
        <Modal onClose={() => setDash(null)}>
          <div style={{ width: 520, maxWidth: "100%", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 12, boxShadow: "var(--shadow-modal)", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Credencial do CRM</div>
              <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 2 }}>
                Pro CRM abrir o portal em iframe. Entregue à equipe do CRMillion.
              </div>
            </div>
            <code style={{ fontSize: 13, color: "var(--ink)", wordBreak: "break-all", padding: "10px 12px", background: "var(--brand-soft)", borderRadius: 8 }}>
              dashid: {dash.dash_id}<br />dashapikey: {dash.dash_key}
            </code>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button style={btn} onClick={() => navigator.clipboard.writeText(`dashid: ${dash.dash_id}
dashapikey: ${dash.dash_key}`)}>
                Copiar
              </button>
              <button style={primaryBtn} onClick={() => setDash(null)}>Fechar</button>
            </div>
          </div>
        </Modal>
      )}

      {error && (
        <div style={{ padding: "10px 14px", fontSize: 13, color: "var(--danger)", border: "1px solid var(--danger)", borderRadius: 8 }}>
          {error}
        </div>
      )}

      {/* Nova conta */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Agentes (conexão vinculada)</label>
          <AgentPicker options={options} value={agentIds} onChange={setAgentIds} max={MAX_AGENTS} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Login</label>
          <input value={login} onChange={(e) => setLogin(e.target.value)} placeholder="loja-do-joao" style={input} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Nome exibido (loja)</label>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Loja do João" style={input} />
        </div>
        <button
          style={{ ...primaryBtn, opacity: canCreate ? 1 : 0.5 }}
          disabled={!canCreate || createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          {createMut.isPending ? "Criando…" : "Criar conta"}
        </button>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando…</div>
      ) : accounts.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Nenhuma conta criada ainda.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {["Loja", "Login", "dashid", "Agentes", "Último acesso", "Status", ""].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} style={{ color: "var(--ink)" }}>
                <td style={td}>{a.display_name}</td>
                <td style={td}><code>{a.login}</code></td>
                <td style={td}><code style={{ fontSize: 11, color: "var(--ink-muted)" }}>{a.id}</code></td>
                <td style={td}><AgentChips agents={a.agents ?? []} /></td>
                <td style={{ ...td, color: "var(--ink-muted)", whiteSpace: "nowrap" }}>
                  {a.last_login_at ? new Date(a.last_login_at + "Z").toLocaleString("pt-BR") : "nunca"}
                </td>
                <td style={td}>
                  <span style={{ fontSize: 12, color: a.disabled ? "var(--danger)" : "var(--ok)" }}>
                    {a.disabled ? "desativada" : "ativa"}
                  </span>
                </td>
                <td style={{ ...td, whiteSpace: "nowrap", textAlign: "right" }}>
                  <button style={{ ...btnSm, marginRight: 6 }} onClick={() => { setError(null); setEditing(a); }}>
                    Editar
                  </button>
                  <button style={{ ...btnSm, marginRight: 6 }} onClick={() => resetMut.mutate(a.id)} disabled={resetMut.isPending}>
                    Resetar senha
                  </button>
                  <button style={{ ...btnSm, marginRight: 6 }} onClick={() => keyMut.mutate({ id: a.id, regen: false })} disabled={keyMut.isPending}>
                    Ver chave
                  </button>
                  <button style={{ ...btnSm, marginRight: 6 }} onClick={() => keyMut.mutate({ id: a.id, regen: true })} disabled={keyMut.isPending}>
                    Regenerar chave
                  </button>
                  <button style={btnSm} onClick={() => toggleMut.mutate(a)} disabled={toggleMut.isPending}>
                    {a.disabled ? "Ativar" : "Desativar"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && <EditAccountModal account={editing} options={options} onClose={() => setEditing(null)} />}
    </div>
  );
}

function AgentChips({ agents }: { agents: AccountAgent[] }) {
  if (agents.length === 0) return <span style={{ color: "var(--danger)", fontSize: 12 }}>sem agente</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {agents.map((g) => (
        <span
          key={g.agent_id}
          title={g.connection_name ? `Conexão: ${g.connection_name}` : "Agente sem conexão WABA vinculada"}
          style={{
            fontSize: 12, padding: "2px 8px", borderRadius: 99, whiteSpace: "nowrap",
            background: g.connection_name ? "var(--surface-hover)" : "var(--danger-soft)",
            border: `1px solid ${g.connection_name ? "var(--line)" : "var(--danger-soft-line)"}`,
            color: g.connection_name ? "var(--ink-secondary)" : "var(--danger)",
          }}
        >
          {g.agent_name ?? g.agent_id}
        </span>
      ))}
    </div>
  );
}

function EditAccountModal({ account, options, onClose }: {
  account: Account; options: AgentOption[]; onClose: () => void;
}) {
  const qc = useQueryClient();
  const current = (account.agents ?? []).map((g) => g.agent_id);
  const [name, setName] = useState(account.display_name);
  const [ids, setIds] = useState<string[]>(current);
  const [error, setError] = useState<string | null>(null);

  // Agente da conta que perdeu a conexão continua na lista (dá pra desmarcar).
  const merged: AgentOption[] = [
    ...options,
    ...(account.agents ?? [])
      .filter((g) => !options.some((o) => o.id === g.agent_id))
      .map((g) => ({ id: g.agent_id, name: g.agent_name ?? g.agent_id, connection: g.connection_name })),
  ];

  const sameAgents = ids.length === current.length && ids.every((id) => current.includes(id));
  const sameName = name.trim() === account.display_name;

  const mut = useMutation({
    mutationFn: () => {
      const body: { display_name?: string; agent_ids?: string[] } = {};
      if (!sameName) body.display_name = name.trim();
      if (!sameAgents) body.agent_ids = ids;
      return wabaApi.patch(`/portal/accounts/${account.id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["helena-accounts"] });
      onClose();
    },
    onError: (e) => setError(errorText(e, "Não foi possível salvar a conta.")),
  });

  const valid = ids.length > 0 && !!name.trim();

  return (
    <Modal onClose={onClose}>
      <div style={{ width: 460, maxWidth: "100%", background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 12, boxShadow: "var(--shadow-modal)", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Editar conta</div>
          <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 2 }}>
            Login <code>{account.login}</code> — a senha não muda.
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Nome exibido (loja)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...input, width: "100%" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Agentes da conta</label>
          <div style={{ border: "1px solid var(--line)", borderRadius: 8, padding: 8 }}>
            <AgentChecklist options={merged} value={ids} onChange={setIds} max={MAX_AGENTS} />
          </div>
        </div>
        {error && <div style={{ fontSize: 12.5, color: "var(--danger)" }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button style={btn} onClick={onClose}>Cancelar</button>
          <button
            style={{ ...primaryBtn, opacity: valid && !(sameName && sameAgents) ? 1 : 0.5 }}
            disabled={!valid || (sameName && sameAgents) || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
